import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Megaphone, Plus, Trash2 } from 'lucide-react';
import type { AnnouncementAdmin, AnnouncementUpsertBody } from '@evi/shared';
import {
  fetchAnnouncementsAdmin, createAnnouncementAdmin, updateAnnouncementAdmin,
  deleteAnnouncementAdmin, fetchFleets,
} from '@/lib/api';
import { localInputToIso, toLocalInput } from '@/lib/datetimeLocal';
import { useAuthStore } from '@/store/auth';
import { ResponsiveTable, type Column } from '@/components/ui/ResponsiveTable';
import { Modal } from '@/components/ui/Modal';
import { useRecentRoute } from '@/hooks/useRecentRoute';
import { usePromotionAccess } from '@/hooks/usePromotionAccess';
import styles from './AnnouncementsPage.module.css';
import { Switch } from '@/components/ui/Switch';

/**
 * Announcements CMS — the dismissible landing popups the driver PWA
 * shows on map-ready. List + create/edit modal, mirroring CouponsPage.
 * Server enforces the Announcement matrix permission and the audience
 * rules (all_drivers / residential_owners are super_admin-only); the
 * form hides what the caller can't use so the 403 never surfaces.
 */

type Liveness = 'live' | 'scheduled' | 'ended' | 'disabled';

function livenessOf(a: AnnouncementAdmin): Liveness {
  if (!a.isActive) return 'disabled';
  const now = Date.now();
  if (a.startsAt && new Date(a.startsAt).getTime() > now) return 'scheduled';
  if (a.endsAt && new Date(a.endsAt).getTime() < now) return 'ended';
  return 'live';
}

const LIVENESS_LABEL: Record<Liveness, string> = {
  live: 'Live',
  scheduled: 'Scheduled',
  ended: 'Ended',
  disabled: 'Disabled',
};

const AUDIENCE_LABEL: Record<AnnouncementAdmin['audience'], string> = {
  all_drivers: 'All drivers',
  tenant: 'This tenant',
  fleet: 'Fleet',
  residential_owners: 'Residential owners',
};

export function AnnouncementsPage() {
  useRecentRoute('Announcements');
  const access=usePromotionAccess('announcements');
  const qc = useQueryClient();
  const { data: announcements = [], isLoading, error } = useQuery({
    queryKey: ['announcements', 'admin'],
    queryFn: fetchAnnouncementsAdmin,
    staleTime: 30_000,
  });

  const [editing, setEditing] = useState<AnnouncementAdmin | null>(null);
  const [creating, setCreating] = useState(false);

  const toggle = useMutation({
    mutationFn: (a: AnnouncementAdmin) =>
      updateAnnouncementAdmin(a.id, { isActive: !a.isActive }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['announcements', 'admin'] }),
  });

  const remove = useMutation({
    mutationFn: deleteAnnouncementAdmin,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['announcements', 'admin'] }),
  });

  const columns: Column<AnnouncementAdmin>[] = [
    {
      key: 'title',
      header: 'Title',
      mobile: 'primary',
      cell: (a) => a.title,
    },
    {
      key: 'audience',
      header: 'Audience',
      mobile: 'secondary',
      cell: (a) => AUDIENCE_LABEL[a.audience],
    },
    {
      key: 'priority',
      header: 'Priority',
      align: 'right',
      mobile: 'body',
      cell: (a) => String(a.priority),
    },
    {
      key: 'window',
      header: 'Window',
      mobile: 'body',
      cell: (a) => formatWindow(a),
    },
    {
      key: 'status',
      header: 'Status',
      mobile: 'body',
      cell: (a) => {
        const liveness = livenessOf(a);
        return (
          <button
            type="button"
            className={`${styles.statusToggle} ${styles[`status_${liveness}`]}`}
            disabled={!access.update}
            title={a.isActive ? 'Click to deactivate' : 'Click to activate'}
            onClick={(e) => {
              e.stopPropagation();
              toggle.mutate(a);
            }}
          >
            {LIVENESS_LABEL[liveness]}
          </button>
        );
      },
    },
    {
      key: 'actions',
      header: '',
      align: 'right',
      mobile: 'body',
      cell: (a) => access.delete ? (
        <button
          type="button"
          title="Delete announcement"
          aria-label={`Delete ${a.title}`}
          disabled={remove.isPending}
          style={{
            background: 'none', border: 'none', cursor: 'pointer',
            color: 'var(--danger, #e5484d)', padding: 4, lineHeight: 0,
          }}
          onClick={(e) => {
            e.stopPropagation();
            if (window.confirm(`Delete "${a.title}"? This can't be undone.`)) {
              remove.mutate(a.id);
            }
          }}
        >
          <Trash2 size={15} />
        </button>
      ) : null,
    },
  ];

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <div className={styles.headerIcon}><Megaphone size={18} /></div>
          <div>
            <h1 className={styles.title}>Announcements</h1>
            <p className={styles.subtitle}>
              Dismissible popups shown to drivers on landing
            </p>
          </div>
        </div>
        {access.create && <button
          type="button"
          className={styles.primaryBtn}
          onClick={() => setCreating(true)}
        >
          <Plus size={16} /> New Announcement
        </button>}
      </div>

      {error && (
        <div className={styles.errorBanner}>
          {error instanceof Error ? error.message : 'Failed to load announcements'}
        </div>
      )}

      {isLoading ? (
        <p className={styles.loading}>Loading…</p>
      ) : (
        <ResponsiveTable
          columns={columns}
          rows={announcements}
          rowKey={(a) => a.id}
          onRowClick={access.update?(a) => setEditing(a):undefined}
          emptyMessage={
            <div className={styles.empty}>
              <p className={styles.emptyTitle}>No announcements yet</p>
              <p className={styles.emptyDesc}>
                {access.create?'Create one to show drivers a promotion, a new feature, or a heads-up about their site.':'Announcements are managed by EV Initiative.'}
              </p>
              {access.create && <button
                type="button"
                className={styles.primaryBtn}
                onClick={() => setCreating(true)}
              >
                <Plus size={16} /> Create your first announcement
              </button>}
            </div>
          }
        />
      )}

      {(creating || editing) && (
        <AnnouncementFormSheet
          announcement={editing}
          onClose={() => { setEditing(null); setCreating(false); }}
          onSaved={() => {
            qc.invalidateQueries({ queryKey: ['announcements', 'admin'] });
            setEditing(null);
            setCreating(false);
          }}
        />
      )}
    </div>
  );
}

// ── Create / edit form ───────────────────────────────────────────────

function AnnouncementFormSheet({
  announcement, onClose, onSaved,
}: {
  announcement: AnnouncementAdmin | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const qc = useQueryClient();
  const user = useAuthStore((s) => s.user);
  const isSuper = user?.role === 'super_admin';

  const [title, setTitle] = useState(announcement?.title ?? '');
  const [body, setBody] = useState(announcement?.body ?? '');
  const [imageUrl, setImageUrl] = useState(announcement?.imageUrl ?? '');
  const [imageOk, setImageOk] = useState(true);
  const [videoUrl, setVideoUrl] = useState(announcement?.videoUrl ?? '');
  const [videoOk, setVideoOk] = useState(true);
  const [ctaLabel, setCtaLabel] = useState(announcement?.ctaLabel ?? '');
  const [ctaLink, setCtaLink] = useState(announcement?.ctaLink ?? '');
  const [audience, setAudience] = useState<AnnouncementUpsertBody['audience']>(
    announcement?.audience ?? 'tenant',
  );
  const [fleetId, setFleetId] = useState(announcement?.fleetId ?? '');
  const [priority, setPriority] = useState(String(announcement?.priority ?? 0));
  const [startsAt, setStartsAt] = useState(toLocalInput(announcement?.startsAt));
  const [endsAt, setEndsAt] = useState(toLocalInput(announcement?.endsAt));
  const [isActive, setIsActive] = useState(announcement?.isActive ?? true);
  const [submitError, setSubmitError] = useState('');

  // Fleet picker — only fetched once the fleet audience is chosen.
  const { data: fleets = [] } = useQuery({
    queryKey: ['fleets', 'admin'],
    queryFn: fetchFleets,
    staleTime: 60_000,
    enabled: audience === 'fleet',
  });

  const save = useMutation({
    mutationFn: async () => {
      const payload: AnnouncementUpsertBody = {
        title,
        body,
        imageUrl: imageUrl.trim() || null,
        videoUrl: videoUrl.trim() || null,
        ctaLabel: ctaLabel.trim() || null,
        ctaLink: ctaLink.trim() || null,
        audience,
        fleetId: audience === 'fleet' ? fleetId || null : null,
        priority: Number(priority) || 0,
        // datetime-local strings carry no zone, and the server (UTC) read
        // them as UTC. Send the instant, in the browser's own time
        // (lib/datetimeLocal.ts).
        startsAt: localInputToIso(startsAt, announcement?.startsAt),
        endsAt: localInputToIso(endsAt, announcement?.endsAt),
        isActive,
      };
      if (announcement) {
        return updateAnnouncementAdmin(announcement.id, payload);
      }
      return createAnnouncementAdmin(payload);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['announcements', 'admin'] });
      onSaved();
    },
    onError: (err) => {
      setSubmitError(err instanceof Error ? err.message : 'Save failed');
    },
  });

  const previewUrl = imageUrl.trim();
  const videoPreviewUrl = videoUrl.trim();

  return (
    <Modal
      isOpen
      onClose={onClose}
      title={announcement ? 'Edit announcement' : 'New Announcement'}
      size="lg"
    >
      <form
        className={styles.form}
        onSubmit={(e) => { e.preventDefault(); setSubmitError(''); save.mutate(); }}
      >
        <label className={styles.field}>
          <span className={styles.label}>Title</span>
          <input
            className={styles.input}
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            required
            maxLength={120}
            placeholder="Charge more, earn more this July"
          />
        </label>

        <label className={styles.field}>
          <span className={styles.label}>Body</span>
          <textarea
            className={styles.input}
            rows={3}
            value={body}
            onChange={(e) => setBody(e.target.value)}
            required
            maxLength={600}
            placeholder="One or two short sentences. Drivers see this on a card over the map."
          />
        </label>

        <label className={styles.field}>
          <span className={styles.label}>Image URL <span className={styles.muted}>(optional)</span></span>
          <input
            className={styles.input}
            type="url"
            value={imageUrl}
            onChange={(e) => { setImageUrl(e.target.value); setImageOk(true); }}
            maxLength={500}
            placeholder="https://…/promo.webp"
          />
        </label>
        {previewUrl && (
          <div className={styles.imagePreview}>
            {imageOk ? (
              <img
                src={previewUrl}
                alt="Announcement preview"
                onError={() => setImageOk(false)}
              />
            ) : (
              <span className={styles.imageBroken}>
                Couldn't load that image — check the URL is public.
              </span>
            )}
          </div>
        )}

        <label className={styles.field}>
          <span className={styles.label}>
            Video URL <span className={styles.muted}>(optional — MP4/WebM, short muted loop; the image doubles as the poster)</span>
          </span>
          <input
            className={styles.input}
            type="url"
            value={videoUrl}
            onChange={(e) => { setVideoUrl(e.target.value); setVideoOk(true); }}
            maxLength={500}
            placeholder="https://…/promo-loop.mp4"
          />
        </label>
        {videoPreviewUrl && (
          <div className={styles.imagePreview}>
            {videoOk ? (
              // Previewed exactly as the driver card plays it: muted,
              // looped, autoplaying, no controls.
              <video
                src={videoPreviewUrl}
                poster={previewUrl || undefined}
                autoPlay
                muted
                loop
                playsInline
                preload="metadata"
                onError={() => setVideoOk(false)}
              />
            ) : (
              <span className={styles.imageBroken}>
                Couldn't play that video — check the URL is public and MP4/WebM.
              </span>
            )}
          </div>
        )}

        <div className={styles.fieldGrid2}>
          <label className={styles.field}>
            <span className={styles.label}>CTA label <span className={styles.muted}>(optional)</span></span>
            <input
              className={styles.input}
              value={ctaLabel}
              onChange={(e) => setCtaLabel(e.target.value)}
              maxLength={40}
              placeholder="See rewards"
            />
          </label>
          <label className={styles.field}>
            <span className={styles.label}>CTA link <span className={styles.muted}>(route or URL)</span></span>
            <input
              className={styles.input}
              value={ctaLink}
              onChange={(e) => setCtaLink(e.target.value)}
              maxLength={500}
              placeholder="/rewards or https://…"
            />
          </label>
        </div>

        <div className={styles.fieldGrid2}>
          <label className={styles.field}>
            <span className={styles.label}>Audience</span>
            <select
              className={styles.input}
              value={audience}
              onChange={(e) => setAudience(e.target.value as AnnouncementUpsertBody['audience'])}
            >
              <option value="tenant">This tenant's drivers</option>
              <option value="fleet">A specific fleet</option>
              {isSuper && <option value="all_drivers">All drivers (platform-wide)</option>}
              {isSuper && <option value="residential_owners">Residential owners</option>}
            </select>
          </label>
          <label className={styles.field}>
            <span className={styles.label}>Priority <span className={styles.muted}>(highest wins)</span></span>
            <input
              className={styles.input}
              type="number"
              min="-1000"
              max="1000"
              value={priority}
              onChange={(e) => setPriority(e.target.value)}
            />
          </label>
        </div>

        {audience === 'fleet' && (
          <label className={styles.field}>
            <span className={styles.label}>Fleet</span>
            <select
              className={styles.input}
              value={fleetId ?? ''}
              onChange={(e) => setFleetId(e.target.value)}
              required
            >
              <option value="" disabled>Choose a fleet…</option>
              {fleets.map((f) => (
                <option key={f._id} value={f._id}>{f.name}</option>
              ))}
            </select>
          </label>
        )}

        <div className={styles.fieldGrid2}>
          <label className={styles.field}>
            <span className={styles.label}>Starts <span className={styles.muted}>(optional)</span></span>
            <input
              className={styles.input}
              type="datetime-local"
              value={startsAt}
              onChange={(e) => setStartsAt(e.target.value)}
            />
          </label>
          <label className={styles.field}>
            <span className={styles.label}>Ends <span className={styles.muted}>(optional)</span></span>
            <input
              className={styles.input}
              type="datetime-local"
              value={endsAt}
              onChange={(e) => setEndsAt(e.target.value)}
            />
          </label>
        </div>

        <div className={styles.toggles}>
          <Switch
            checked={isActive}
            onChange={(e) => setIsActive(e.target.checked)}
            label="Active: eligible drivers see this on their next landing"
          />
        </div>

        {submitError && <div className={styles.errorBanner}>{submitError}</div>}

        <div className={styles.actions}>
          <button type="button" className={styles.secondaryBtn} onClick={onClose}>
            Cancel
          </button>
          <button
            type="submit"
            className={styles.primaryBtn}
            disabled={save.isPending}
          >
            {save.isPending ? 'Saving…' : announcement ? 'Save changes' : 'Create announcement'}
          </button>
        </div>
      </form>
    </Modal>
  );
}

// ── Formatters ───────────────────────────────────────────────────────

function formatWindow(a: AnnouncementAdmin): string {
  const from = a.startsAt ? formatDate(a.startsAt) : null;
  const to = a.endsAt ? formatDate(a.endsAt) : null;
  if (from && to) return `${from} – ${to}`;
  if (from) return `From ${from}`;
  if (to) return `Until ${to}`;
  return 'Always';
}

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      month: 'short', day: 'numeric', year: 'numeric',
    });
  } catch {
    return iso;
  }
}
